<template>
  <div class="dna-container" :lang="$i18n.locale">
    <!-- DNA 双螺旋背景 -->
    <div class="dna-background">
      <video
        ref="bannerVideo"
        class="banner-video"
        autoplay
        muted
        loop
        playsinline
        poster="http://www.healthcode.com/assets/images-next/hero-poster.webp"
      >
        <source
          src="http://www.healthcode.com//assets/videos/dna.mp4"
          type="video/mp4"
        />
        <source
          src="http://www.healthcode.com//assets/videos/dna.mp4"
          type="video/webm"
        />
        {{ $t("home.dnaLayout.videoFallback") }}
      </video>
    </div>
    <div class="main-container">
      <div class="info-panel">
        <h1>{{ $t("home.dnaLayout.title") }}</h1>
        <div class="subtitle">
          <h2>{{ $t("home.dnaLayout.subtitle") }}</h2>
          <p>
            {{ $t("home.dnaLayout.description") }}
          </p>
        </div>
      </div>

      <div class="dot-navigation">
        <div
          class="dot-item dot-item-0"
          :class="{ active: activeDotIndex === 0 }"
          @click="selectContent(0)"
        >
          <div class="dot">
            <div class="dot-inner"></div>
          </div>
          <span class="label">{{
            $t("home.dnaLayout.labels.proteinFactory")
          }}</span>
        </div>

        <div
          class="dot-item dot-item-1"
          :class="{ active: activeDotIndex === 1 }"
          @click="selectContent(1)"
        >
          <div class="dot">
            <div class="dot-inner"></div>
          </div>
          <span class="label">{{
            $t("home.dnaLayout.labels.desertLight")
          }}</span>
        </div>

        <div
          class="dot-item dot-item-2"
          :class="{ active: activeDotIndex === 2 }"
          @click="selectContent(2)"
        >
          <div class="dot">
            <div class="dot-inner"></div>
          </div>
          <span class="label">{{
            $t("home.dnaLayout.labels.artificialBlood")
          }}</span>
        </div>

        <div
          class="dot-item dot-item-3"
          :class="{ active: activeDotIndex === 3 }"
          @click="selectContent(3)"
        >
          <div class="dot">
            <div class="dot-inner"></div>
          </div>
          <span class="label">{{
            $t("home.dnaLayout.labels.antiCancerDrug")
          }}</span>
        </div>
        <!-- 在现有的第四个圆点后添加 -->
        <div
          class="dot-item dot-item-4"
          :class="{ active: activeDotIndex === 4 }"
          @click="selectContent(4)"
        >
          <div class="dot">
            <div class="dot-inner"></div>
          </div>
          <span class="label">{{
            $t("home.dnaLayout.labels.sweetProtein")
          }}</span>
        </div>
      </div>

      <div class="connection-svg-container" style="display: none">
        <svg
          class="connection-svg"
          width="215"
          height="41"
          viewBox="0 0 215 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.333333 3C0.333333 4.47276 1.52724 5.66667 3 5.66667C4.47276 5.66667 5.66667 4.47276 5.66667 3C5.66667 1.52724 4.47276 0.333333 3 0.333333C1.52724 0.333333 0.333333 1.52724 0.333333 3ZM76 37.5L75.7864 37.9521L75.8878 38H76V37.5ZM209.333 37.5C209.333 38.9728 210.527 40.1667 212 40.1667C213.473 40.1667 214.667 38.9728 214.667 37.5C214.667 36.0272 213.473 34.8333 212 34.8333C210.527 34.8333 209.333 36.0272 209.333 37.5ZM3 3L2.78636 3.45206L75.7864 37.9521L76 37.5L76.2136 37.0479L3.21364 2.54794L3 3ZM76 37.5V38H212V37.5V37H76V37.5Z"
            fill="white"
            fill-opacity="0.41"
          />
        </svg>
      </div>

      <!-- 内容展示框 -->
      <div
        class="content-box"
        ref="contentBox"
        :style="{ bottom: contentBoxBottom + 'rem' }"
      >
        <div
          v-for="(item, index) in contentList"
          :key="index"
          class="content-item"
          :class="{ active: activeContentIndex === index }"
        >
          <h3>{{ item.title }}</h3>
          <p v-html="item.description"></p>
          <div
            v-if="item.images && item.images.length > 0"
            class="image-gallery"
          >
            <div
              v-for="(img, imgIndex) in item.images"
              :key="imgIndex"
              class="image-container"
              @mouseenter="handleMouseEnter(img, $event)"
              @mousemove="movePreview($event)"
              @mouseleave="handleMouseLeave"
              @touchstart="handleTouchStart(img, $event)"
              @touchend="handleTouchEnd"
              @click="handleImageClick(img, imgIndex, item.images)"
            >
              <img
                :src="img"
                :alt="$t('home.dnaLayout.imageAlt', { index: imgIndex + 1 })"
                class="thumbnail"
              />
              <div class="image-overlay">
                <!-- 鼠标悬停时显示放大效果 -->
                <div class="zoom-hint"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 图片悬停放大预览框（桌面端） -->
      <div
        v-if="hoveredImage && !isMobile"
        class="image-hover-preview"
        :style="{
          top: previewPosition.y + 'px',
          left: previewPosition.x + 'px',
        }"
      >
        <img :src="hoveredImage" :alt="hoveredImageAlt" class="preview-image" />
      </div>

      <!-- 图片放大模态框（移动端） -->
      <div
        v-if="showImageModal"
        class="image-modal"
        @click.self="closeImageModal"
      >
        <div class="modal-content">
          <!-- 关闭按钮 -->
          <button class="modal-close" @click="closeImageModal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M6 6L18 18"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <!-- 图片容器 -->
          <div class="modal-image-container">
            <img
              :src="currentImage"
              :alt="currentImageAlt"
              class="modal-image"
              ref="modalImage"
            />
          </div>

          <!-- 图片信息 -->
          <div class="image-info">
            <div class="image-counter">
              {{ currentImageIndex + 1 }} / {{ totalImages }}
            </div>
          </div>

          <!-- 导航按钮 -->
          <button
            v-if="totalImages > 1"
            class="nav-button prev-button"
            @click.stop="prevImage"
            :disabled="currentImageIndex === 0"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            v-if="totalImages > 1"
            class="nav-button next-button"
            @click.stop="nextImage"
            :disabled="currentImageIndex === totalImages - 1"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import rzximg1 from "../../assets/image/home/cellfree.webp";
import rzximg2 from "../../assets/image/home/renzx2.webp";
import rzximg3 from "../../assets/image/home/renzx3.webp";
import rzximg4 from "../../assets/image/home/rzx4.png";
import proteinimg1 from "../../assets/image/home/home_d2p_4.png";
import proteinimg2 from "../../assets/image/home/home_d2p_5.png";
import proteinimg3 from "../../assets/image/home/home_d2p_6.png";
import proteinimg4 from "../../assets/image/home/home_d2p_7.png";
import smzgimg1 from "../../assets/image/home/home_d2p_8.png";
import smzgimg2 from "../../assets/image/home/home_d2p_9.png";
import smzgimg3 from "../../assets/image/home/home_d2p_10.png";
import smzgimg4 from "../../assets/image/home/home_d2p_11.png";
import sweetimg1 from "../../assets/image/home/home_d2p_12.png";
import sweetimg2 from "../../assets/image/home/home_d2p_13.png";
import sweetimg3 from "../../assets/image/home/home_d2p_14.png";
import sweetimg4 from "../../assets/image/home/home_d2p_15.png";
import drugimg1 from "../../assets/image/home/home_d2p_1.png";
import drugimg2 from "../../assets/image/home/home_d2p_2.png";
import drugimg3 from "../../assets/image/home/home_d2p_3.png";
export default defineComponent({
  name: "DnaLayout",
  data() {
    return {
      activeDotIndex: 0,
      activeContentIndex: 0,
      contentBoxBottom: 10, // 默认底部距离
      labelContentMap: {
        artificialBlood: 2,
        desertLight: 1,
        antiCancerDrug: 4,
        proteinFactory: 0,
        sweetProtein: 3,
      },
      // 图片悬停功能相关数据（桌面端）
      hoveredImage: "",
      hoveredImageAlt: "",
      previewPosition: { x: 0, y: 0 },
      // 图片点击放大功能相关数据（移动端）
      showImageModal: false,
      currentImage: "",
      currentImageIndex: 0,
      currentImages: [],
      currentImageAlt: "",
      currentImageDescription: "",
      // 设备检测
      isMobile: false,
      // 触摸相关
      touchStartTime: 0,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  computed: {
    contentList() {
      return [
        {
          title: this.$t("home.dnaLayout.content.proteinFactory.title"),
          description: this.$t(
            "home.dnaLayout.content.proteinFactory.description"
          ),
          images: [proteinimg1, proteinimg2, proteinimg3, proteinimg4],
          imageDescriptions: [
            this.$t("home.dnaLayout.imageDescriptions.proteinFactory.image1"),
            this.$t("home.dnaLayout.imageDescriptions.proteinFactory.image2"),
            this.$t("home.dnaLayout.imageDescriptions.proteinFactory.image3"),
            this.$t("home.dnaLayout.imageDescriptions.proteinFactory.image4"),
          ],
        },
        {
          title: this.$t("home.dnaLayout.content.desertLight.title"),
          description: this.$t(
            "home.dnaLayout.content.desertLight.description"
          ),
          images: [smzgimg1, smzgimg2, smzgimg3, smzgimg4],
          imageDescriptions: [
            this.$t("home.dnaLayout.imageDescriptions.desertLight.image1"),
            this.$t("home.dnaLayout.imageDescriptions.desertLight.image2"),
            this.$t("home.dnaLayout.imageDescriptions.desertLight.image3"),
          ],
        },
        {
          title: this.$t("home.dnaLayout.content.artificialBlood.title"),
          description: this.$t(
            "home.dnaLayout.content.artificialBlood.description"
          ),
          images: [rzximg1, rzximg2, rzximg3,rzximg4],
          imageDescriptions: [
            this.$t("home.dnaLayout.imageDescriptions.artificialBlood.image1"),
            this.$t("home.dnaLayout.imageDescriptions.artificialBlood.image2"),
            this.$t("home.dnaLayout.imageDescriptions.artificialBlood.image3"),
          ],
        },
        {
          title: this.$t("home.dnaLayout.content.antiCancerDrug.title"),
          description: this.$t(
            "home.dnaLayout.content.antiCancerDrug.description"
          ),
          images: [drugimg1,drugimg2,drugimg3],
          imageDescriptions: [],
        },
        {
          title: this.$t("home.dnaLayout.content.sweetProtein.title"),
          description: this.$t(
            "home.dnaLayout.content.sweetProtein.description"
          ),
          images: [sweetimg1, sweetimg2,sweetimg3,sweetimg4],
          imageDescriptions: [
            this.$t("home.dnaLayout.imageDescriptions.sweetProtein.image1"),
          ],
        },
      ];
    },
    // 计算总图片数
    totalImages() {
      return this.currentImages.length;
    },
  },
  mounted() {
    // 初始计算内容框位置
    this.$nextTick(() => {
      this.calculateContentBoxPosition();
       this.adjustDotPositions(); 
    });

    window.addEventListener("resize", this.handleResize);
    // 添加键盘事件监听
    document.addEventListener("keydown", this.handleKeydown);

    // 检测是否为移动设备
    this.checkIfMobile();
    window.addEventListener("resize", this.checkIfMobile);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    // 移除键盘事件监听
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.checkIfMobile);
  },
  methods: {
    selectContent(dotIndex) {
      this.activeDotIndex = dotIndex;
      this.activeContentIndex = dotIndex;

      this.$nextTick(() => {
        this.calculateContentBoxPosition();

        // 在移动端，切换内容后也调整圆点位置
        if (window.innerWidth < 768) {
          this.adjustDotPositionsForMobile();
        }
      });
    },

    // 计算内容框位置
    calculateContentBoxPosition() {
      if (this.$refs.contentBox) {
        const contentBoxHeight = this.$refs.contentBox.offsetHeight;
        const heightInRem = contentBoxHeight / 16;
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        // 根据屏幕尺寸调整底部距离
        if (screenWidth >= 1920) {
          // 大屏幕
          this.contentBoxBottom = Math.max(6, 20 - heightInRem * 0.2);
        } else if (screenWidth >= 1440) {
          // 中等屏幕
          this.contentBoxBottom = Math.max(4, 15 - heightInRem * 0.25);
        } else if (screenWidth >= 768) {
          // 平板
          this.contentBoxBottom = Math.max(3, 12 - heightInRem * 0.3);
        } else {
          // 手机
          const maxHeightRatio = (screenHeight / 16) * 0.3; // 最大占用屏幕高度的30%
          this.contentBoxBottom = Math.max(
            1,
            Math.min(8, maxHeightRatio - heightInRem * 0.4)
          );
        }

        // 移动端，当内容框位置改变时，重新调整圆点位置
        if (screenWidth < 768) {
          this.adjustDotPositionsForMobile();
        }
      }
    },
  adjustMobileDotPositions() {
    // 只在移动端执行
    if (window.innerWidth >= 768) return;
    
    this.$nextTick(() => {
      const contentBox = this.$refs.contentBox;
      if (contentBox) {
        const screenHeight = window.innerHeight;
        const contentBoxElement = contentBox.$el || contentBox;
        const contentBoxRect = contentBoxElement.getBoundingClientRect();
        const contentBoxTop = contentBoxRect.top;
        const contentBoxHeight = contentBoxRect.height;
        
        // 计算内容框占据的屏幕百分比
        const contentBoxTopPercent = (contentBoxTop / screenHeight) * 100;
        const contentBoxBottomPercent = ((contentBoxTop + contentBoxHeight) / screenHeight) * 100;
        
        // 为所有圆点设置合适的位置，避开内容框区域
        for (let i = 0; i <= 4; i++) {
          const dotItem = document.querySelector(`.dot-item-${i}`);
          if (dotItem) {
            // 计算圆点的安全 top 位置，确保不在内容框区域内
            let safeTop = 40; // 默认位置
            
            // 如果内容框在中间位置，调整圆点位置避开它
            if (contentBoxTopPercent < 70 && contentBoxBottomPercent > 30) {
              safeTop = Math.min(30, contentBoxTopPercent - 20);
            }
            
            // 应用计算出的安全位置
            dotItem.style.top = `${safeTop}%`;
          }
        }
      }
    });
  },
  // 动态调整圆点位置的方法
  adjustDotPositions() {
 
    this.$nextTick(() => {
      const contentBox = this.$refs.contentBox;
      if (contentBox && contentBox.$el) {
        const contentBoxElement = contentBox.$el;
        const contentBoxRect = contentBoxElement.getBoundingClientRect();
        const screenHeight = window.innerHeight;
        
        // 计算内容框的位置百分比
        const contentBoxTopPercent = (contentBoxRect.top / screenHeight) * 100;
        const contentBoxBottomPercent = ((contentBoxRect.top + contentBoxRect.height) / screenHeight) * 100;
        
        // 为每个圆点计算安全位置
        for (let i = 0; i <= 4; i++) {
          const dotItem = document.querySelector(`.dot-item-${i}`);
          if (dotItem) {
            // 计算圆点的推荐位置，避开内容框区域
            let recommendedTop;
            
            // 如果内容框在下半部分，圆点放在上半部分；反之亦然
            if (contentBoxTopPercent > 50) {
              // 内容框在下半部分，圆点可以放在上半部分
              recommendedTop = Math.min(30, contentBoxTopPercent - 20);
            } else {
              // 内容框在上半部分，圆点可以放在下半部分
              recommendedTop = Math.max(70, contentBoxBottomPercent + 10);
            }
            
            // 设置圆点位置，限制在合理范围内
            dotItem.style.top = `${Math.max(10, Math.min(90, recommendedTop))}%`;
          }
        }
      }
    });
  },
    // 调整移动端圆点位置以避免被内容框遮挡
    adjustDotPositionsForMobile() {
      this.$nextTick(() => {
        const contentBox = this.$refs.contentBox;
        if (contentBox) {
          const contentBoxRect = contentBox.getBoundingClientRect();
          const screenHeight = window.innerHeight;

          // 计算内容框顶部相对于屏幕的位置
          const contentBoxTop = screenHeight - this.contentBoxBottom * 16;

          // 根据内容框位置调整圆点位置，
          const dotItem0 = document.querySelector(".dot-item-0");
          if (dotItem0) {
            const maxTopPosition = (contentBoxTop / screenHeight) * 100 - 10;
            if (maxTopPosition > 25) {
              // dotItem0.style.top = `${Math.min(65, maxTopPosition)}%`;
            }
          }
        }
      });
    },

    // 处理窗口大小变化
    handleResize() {
      this.$nextTick(() => {
        this.calculateContentBoxPosition();
         this.adjustDotPositions();
      });
    },

    // 检测是否为移动设备
    checkIfMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    // 桌面端鼠标悬停功能
    handleMouseEnter(img, event) {
      // 如果是移动端，不执行悬停预览
      if (this.isMobile) return;

      this.hoveredImage = img;
      this.hoveredImageAlt = this.$t("home.dnaLayout.hoverPreview");

      // 计算预览框位置
      this.updatePreviewPosition(event);
    },

    handleMouseLeave() {
      // 如果是移动端，不执行悬停预览
      if (this.isMobile) return;

      this.hoveredImage = "";
      this.hoveredImageAlt = "";
    },

    // 移动预览框跟随鼠标
    movePreview(event) {
      // 如果是移动端，不执行悬停预览
      if (this.isMobile) return;

      this.updatePreviewPosition(event);
    },

    updatePreviewPosition(event) {
      if (!event) return;

      const mouseX = event.clientX;
      const mouseY = event.clientY;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // 根据屏幕尺寸动态调整预览框尺寸
      let previewWidth, previewHeight;

      if (screenWidth >= 2560) {
        // 超宽屏幕
        previewWidth = 900;
        previewHeight = 675;
      } else if (screenWidth >= 1920) {
        // 大屏幕
        previewWidth = 800;
        previewHeight = 600;
      } else {
        previewWidth = 600;
        previewHeight = 450;
      }

      // 计算预览框位置，确保在屏幕内
      let posX = mouseX + 30; 
      let posY = mouseY + 30; 

      // 如果右侧空间不足，则在左侧显示
      if (posX + previewWidth > screenWidth) {
        posX = mouseX - previewWidth - 30;
      }

      // 如果底部空间不足，则在上方显示
      if (posY + previewHeight > screenHeight) {
        posY = mouseY - previewHeight - 30;
      }

      // 确保预览框不会超出屏幕左侧和顶部
      posX = Math.max(10, posX);
      posY = Math.max(10, posY);

      this.previewPosition = { x: posX, y: posY };
    },

    // 移动端触摸事件处理
    handleTouchStart(img, event) {
      this.touchStartTime = Date.now();
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    },

    handleTouchEnd() {
      // 触摸结束，重置触摸数据
      this.touchStartTime = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
    },

    // 图片点击处理（移动端和桌面端都可用）
    handleImageClick(img, index, images) {
   
      if (this.isMobile) {
        this.openImageModal(img, index, images);
      }
      else if (Date.now() - this.touchStartTime < 200) {
        this.openImageModal(img, index, images);
      }
    },

    // 图片放大模态框功能（移动端）
    openImageModal(img, index, images) {
      this.currentImage = img;
      this.currentImageIndex = index;
      this.currentImages = images;
      this.currentImageAlt = this.$t("home.dnaLayout.imageAlt", {
        index: index + 1,
      });

      // 获取当前内容的图片描述
      const currentContent = this.contentList[this.activeContentIndex];
      if (
        currentContent &&
        currentContent.imageDescriptions &&
        currentContent.imageDescriptions[index]
      ) {
        this.currentImageDescription = currentContent.imageDescriptions[index];
      } else {
        this.currentImageDescription = "";
      }

      this.showImageModal = true;

      // 禁止背景滚动
      document.body.style.overflow = "hidden";
    },

    closeImageModal() {
      this.showImageModal = false;
      this.currentImage = "";
      this.currentImageIndex = 0;
      this.currentImages = [];
      this.currentImageAlt = "";
      this.currentImageDescription = "";

      // 恢复背景滚动
      document.body.style.overflow = "";
    },

    nextImage() {
      if (this.currentImageIndex < this.totalImages - 1) {
        this.currentImageIndex++;
        this.currentImage = this.currentImages[this.currentImageIndex];
        this.currentImageAlt = this.$t("home.dnaLayout.imageAlt", {
          index: this.currentImageIndex + 1,
        });

        // 更新图片描述
        const currentContent = this.contentList[this.activeContentIndex];
        if (
          currentContent &&
          currentContent.imageDescriptions &&
          currentContent.imageDescriptions[this.currentImageIndex]
        ) {
          this.currentImageDescription =
            currentContent.imageDescriptions[this.currentImageIndex];
        } else {
          this.currentImageDescription = "";
        }
      }
    },

    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
        this.currentImage = this.currentImages[this.currentImageIndex];
        this.currentImageAlt = this.$t("home.dnaLayout.imageAlt", {
          index: this.currentImageIndex + 1,
        });

        // 更新图片描述
        const currentContent = this.contentList[this.activeContentIndex];
        if (
          currentContent &&
          currentContent.imageDescriptions &&
          currentContent.imageDescriptions[this.currentImageIndex]
        ) {
          this.currentImageDescription =
            currentContent.imageDescriptions[this.currentImageIndex];
        } else {
          this.currentImageDescription = "";
        }
      }
    },

    // 键盘事件处理
    handleKeydown(event) {
      if (!this.showImageModal) return;

      switch (event.key) {
        case "Escape":
          this.closeImageModal();
          break;
        case "ArrowLeft":
          this.prevImage();
          break;
        case "ArrowRight":
          this.nextImage();
          break;
      }
    },
  },
});
</script>

<style scoped>
  @import "../../assets/css/dnaSyetem.css";
</style>
